// ── Detail Panel — slides in from right ──

import { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { GraphNode, GraphData } from "../types/graph";
import {
  severityColor,
  severityLabel,
  severityBadgeClass,
} from "../utils/colors";
import PhotoGallery from "./PhotoGallery";
import TrendSparkline from "./TrendSparkline";
import GeoHeatmap from "./GeoHeatmap";
import DeteriorationList from "./DeteriorationList";
import { SeverityDistribution } from "./SeverityDistribution";
import { TopItemsList } from "./TopItemsList";
import {
  useTrend,
  usePhotos,
  useGeoCells,
  useDeteriorating,
  useAreaStats,
  useInspectionDetail,
} from "../hooks/useGraphData";

interface DetailPanelProps {
  node: GraphNode | null;
  graphData: GraphData;
  onClose: () => void;
}

// ── Helpers ──

/** Resolve a link endpoint to its string id */
function linkId(endpoint: string | object): string {
  return typeof endpoint === "string"
    ? endpoint
    : (endpoint as unknown as GraphNode).id;
}

/** Find parent campaign id for an area node */
function findParentCampaignId(
  nodeId: string,
  graphData: GraphData,
): string | null {
  const parentLink = graphData.links.find((l) => linkId(l.target) === nodeId);
  return parentLink ? linkId(parentLink.source) : null;
}

/** Format an ISO date string to a readable format */
function fmtDate(d: string | null | undefined): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

// ── Main Component ──

export default function DetailPanel({
  node,
  graphData,
  onClose,
}: DetailPanelProps) {
  const siteId = node?.type === "site" ? node.id : null;
  const recordId = node?.meta?.recordId ?? null;
  const isInspection = node?.type === "inspection";
  const isArea = node?.type === "area";
  const isCampaign = node?.type === "campaign";

  // Derive campaign context for area nodes
  const parentCampaignId = useMemo(
    () => (isArea && node ? findParentCampaignId(node.id, graphData) : null),
    [isArea, node, graphData],
  );

  // For non-inspection nodes, walk children to find a recordId for photo preview
  const childRecordId = useMemo(() => {
    if (!node || recordId) return null;
    const childIds = new Set(
      graphData.links
        .filter((l) => linkId(l.source) === node.id)
        .map((l) => linkId(l.target)),
    );
    const deepChildIds = new Set<string>();
    for (const id of childIds) {
      const child = graphData.nodes.find((n) => n.id === id);
      if (child?.meta?.recordId) return child.meta.recordId;
      for (const link of graphData.links) {
        if (linkId(link.source) === id) deepChildIds.add(linkId(link.target));
      }
    }
    for (const id of deepChildIds) {
      const child = graphData.nodes.find((n) => n.id === id);
      if (child?.meta?.recordId) return child.meta.recordId;
    }
    return null;
  }, [node, recordId, graphData]);

  // Children summary for non-leaf nodes
  const childSummary = useMemo(() => {
    if (!node) return [];
    const childIds = graphData.links
      .filter((l) => linkId(l.source) === node.id)
      .map((l) => linkId(l.target));
    return graphData.nodes.filter((n) => childIds.includes(n.id));
  }, [node, graphData]);

  // ── Data hooks ──
  const { data: trend } = useTrend(siteId);
  const { data: photos } = usePhotos(recordId ?? childRecordId);
  const campaignId = isCampaign ? node!.id : null;
  const { data: geoCells } = useGeoCells(campaignId);
  const { data: deteriorating } = useDeteriorating(campaignId);
  const { data: areaStats, isLoading: areaStatsLoading } = useAreaStats(
    parentCampaignId,
    isArea ? node!.label : null,
  );
  const { data: inspDetail } = useInspectionDetail(
    isInspection ? node!.id : null,
  );

  // Merge enriched inspection data with node meta
  const lat = inspDetail?.lat ?? node?.meta?.lat;
  const lon = inspDetail?.lon ?? node?.meta?.lon;
  const status = inspDetail?.status ?? node?.meta?.status;
  const period = inspDetail?.period ?? node?.meta?.period;
  const prevInsp = inspDetail?.previousInspection;

  return (
    <AnimatePresence>
      {node && (
        <motion.div
          key="detail-panel"
          initial={{ x: 400, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 400, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed top-0 right-0 h-full w-[480px] max-w-[90vw] z-40
                     bg-white border-l border-fl-evergreen/20
                     shadow-xl overflow-y-auto"
        >
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center
                       rounded-full bg-fl-sand/40 hover:bg-fl-sand transition-colors
                       text-fl-evergreen/60 hover:text-fl-evergreen"
          >
            ✕
          </button>

          <div className="p-6 pt-8 space-y-6">
            {/* ── Header ── */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-widest font-semibold text-fl-forest/60 bg-fl-forest/10 px-2 py-0.5 rounded">
                  {node.type}
                </span>
                {status && (
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-fl-leaf bg-fl-leaf/10 px-2 py-0.5 rounded">
                    {status}
                  </span>
                )}
              </div>
              <h2 className="text-xl font-semibold text-fl-evergreen mt-1 leading-tight">
                {node.label}
              </h2>
              {node.severity !== undefined && (
                <div className="flex items-center gap-3 mt-3">
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${severityBadgeClass(node.severity)}`}
                  >
                    {severityLabel(node.severity)}
                  </span>
                  <div className="flex-1 h-2 rounded-full bg-fl-sand/40 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${(node.severity / 5) * 100}%`,
                        backgroundColor: severityColor(node.severity),
                      }}
                    />
                  </div>
                  <span className="text-sm text-fl-evergreen/60 tabular-nums">
                    {node.severity.toFixed(1)}
                  </span>
                </div>
              )}
            </div>

            {/* ── Inspection Detail Card ── */}
            {isInspection && (
              <div className="rounded-lg border border-fl-evergreen/10 bg-fl-sand/20 p-4 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {node.meta?.rating && (
                    <MetaRow label="Rating" value={node.meta.rating} />
                  )}
                  {node.meta?.priority && (
                    <MetaRow label="Priority" value={node.meta.priority} />
                  )}
                  {(inspDetail?.defectCode ?? node.meta?.defectCode) && (
                    <MetaRow
                      label="Defect code"
                      value={inspDetail?.defectCode ?? node.meta?.defectCode ?? ""}
                    />
                  )}
                  {(inspDetail?.impactType ?? node.meta?.impactType) && (
                    <MetaRow
                      label="Impact type"
                      value={inspDetail?.impactType ?? node.meta?.impactType ?? ""}
                    />
                  )}
                  {(inspDetail?.defectType ?? node.meta?.defectType) && (
                    <MetaRow
                      label="Defect type"
                      value={inspDetail?.defectType ?? node.meta?.defectType ?? ""}
                    />
                  )}
                  {(inspDetail?.riskLevel ?? node.meta?.riskLevel) && (
                    <MetaRow
                      label="Risk level"
                      value={inspDetail?.riskLevel ?? node.meta?.riskLevel ?? ""}
                    />
                  )}
                  {(inspDetail?.riskScore ?? node.meta?.riskScore) != null && (
                    <MetaRow
                      label="Risk score"
                      value={String(inspDetail?.riskScore ?? node.meta?.riskScore)}
                    />
                  )}
                  {(inspDetail?.rectificationPeriod ?? node.meta?.rectificationPeriod) && (
                    <MetaRow
                      label="Rectification"
                      value={inspDetail?.rectificationPeriod ?? node.meta?.rectificationPeriod ?? ""}
                    />
                  )}
                  {(inspDetail?.dueDate ?? node.meta?.dueDate) && (
                    <MetaRow
                      label="Due date"
                      value={fmtDate(inspDetail?.dueDate ?? node.meta?.dueDate)}
                    />
                  )}
                  {(inspDetail?.geoArea ?? node.meta?.geoArea) && (
                    <MetaRow
                      label="Geo area"
                      value={inspDetail?.geoArea ?? node.meta?.geoArea ?? ""}
                    />
                  )}
                  {((inspDetail?.trades ?? node.meta?.trades)?.length ?? 0) > 0 && (
                    <MetaRow
                      label="Trades"
                      value={(inspDetail?.trades ?? node.meta?.trades ?? []).join(", ")}
                    />
                  )}
                  {node.meta?.category && (
                    <MetaRow label="Category" value={node.meta.category} />
                  )}
                  {node.meta?.element && (
                    <MetaRow label="Element" value={node.meta.element} />
                  )}
                  {node.meta?.area && (
                    <MetaRow label="Area" value={node.meta.area} />
                  )}
                  {node.meta?.inspector && (
                    <MetaRow label="Inspector" value={node.meta.inspector} />
                  )}
                  {node.meta?.createdAt && (
                    <MetaRow label="Date" value={fmtDate(node.meta.createdAt)} />
                  )}
                  {period && <MetaRow label="Period" value={period} />}
                </div>

                {/* Location with map link */}
                {lat != null && lon != null && (
                  <div className="flex items-center gap-2 pt-1 border-t border-fl-evergreen/10">
                    <span className="text-fl-evergreen/50 text-xs">📍</span>
                    <a
                      href={`https://www.google.com/maps?q=${lat},${lon}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-fl-forest underline hover:text-fl-leaf transition-colors"
                    >
                      {Number(lat).toFixed(5)}, {Number(lon).toFixed(5)}
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* ── Description ── */}
            {node.meta?.description && (
              <div>
                <SectionHeading>Description</SectionHeading>
                <p className="text-sm text-fl-evergreen/80 leading-relaxed bg-fl-sand/10 rounded p-3">
                  {node.meta.description}
                </p>
              </div>
            )}

            {/* ── Previous Inspection Comparison ── */}
            {prevInsp && (
              <div>
                <SectionHeading>Previous Inspection</SectionHeading>
                <div className="rounded-lg border border-fl-evergreen/10 bg-fl-sand/10 p-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-fl-evergreen/60">Severity</span>
                    <div className="flex items-center gap-2">
                      {prevInsp.severity != null && (
                        <span
                          className="text-xs font-medium px-1.5 py-0.5 rounded"
                          style={{
                            color: severityColor(prevInsp.severity),
                            backgroundColor: `${severityColor(prevInsp.severity)}15`,
                          }}
                        >
                          {prevInsp.severity}
                        </span>
                      )}
                      <span className="text-fl-evergreen/40">→</span>
                      {node.severity !== undefined && (
                        <span
                          className="text-xs font-medium px-1.5 py-0.5 rounded"
                          style={{
                            color: severityColor(node.severity),
                            backgroundColor: `${severityColor(node.severity)}15`,
                          }}
                        >
                          {node.severity.toFixed(0)}
                        </span>
                      )}
                    </div>
                  </div>
                  <MetaRow label="Date" value={fmtDate(prevInsp.createdAt)} />
                  {prevInsp.rating && (
                    <MetaRow label="Rating" value={prevInsp.rating} />
                  )}
                  {prevInsp.status && (
                    <MetaRow label="Status" value={prevInsp.status} />
                  )}
                </div>
              </div>
            )}

            {/* ── Area / Campaign meta rows ── */}
            {!isInspection && (
              <div className="space-y-2 text-sm">
                {node.meta?.client && (
                  <MetaRow label="Client" value={node.meta.client} />
                )}
                {node.meta?.period && (
                  <MetaRow label="Period" value={node.meta.period} />
                )}
                {node.meta?.total !== undefined && (
                  <MetaRow label="Records" value={String(node.meta.total)} />
                )}
                {node.meta?.avgSeverity !== undefined && (
                  <MetaRow
                    label="Avg Severity"
                    value={node.meta.avgSeverity.toFixed(1)}
                    color={severityColor(node.meta.avgSeverity)}
                  />
                )}
                {node.meta?.maxSeverity !== undefined && (
                  <MetaRow
                    label="Max Severity"
                    value={String(node.meta.maxSeverity)}
                    color={severityColor(node.meta.maxSeverity)}
                  />
                )}
              </div>
            )}

            {/* ── Area Stats Section ── */}
            {isArea && areaStats && (
              <>
                {/* Date range */}
                {areaStats.dateRange && (
                  <div className="flex items-center gap-2 text-xs text-fl-evergreen/60">
                    <span>📅</span>
                    <span>
                      {fmtDate(areaStats.dateRange.earliest)} — {fmtDate(areaStats.dateRange.latest)}
                    </span>
                  </div>
                )}

                {/* Severity distribution bars */}
                <SeverityDistribution
                  distribution={areaStats.severityDistribution}
                  total={areaStats.total}
                />

                {/* Top categories */}
                <TopItemsList
                  title="Top Defect Categories"
                  items={areaStats.topCategories}
                />

                {/* Top elements */}
                <TopItemsList
                  title="Top Elements"
                  items={areaStats.topElements}
                />
              </>
            )}
            {isArea && areaStatsLoading && (
              <div className="flex items-center justify-center py-4">
                <div className="w-5 h-5 border-2 border-fl-leaf/30 border-t-fl-leaf rounded-full animate-spin" />
                <span className="ml-2 text-xs text-fl-evergreen/50">
                  Loading area stats…
                </span>
              </div>
            )}

            {/* ── Trend chart for sites ── */}
            {trend && trend.length > 1 && (
              <div>
                <SectionHeading>Severity Trend</SectionHeading>
                <TrendSparkline data={trend} />
              </div>
            )}

            {/* ── Geo heatmap for campaigns ── */}
            {geoCells && geoCells.length > 0 && (
              <div>
                <GeoHeatmap cells={geoCells} />
              </div>
            )}

            {/* ── Deteriorating assets for campaigns ── */}
            {deteriorating && deteriorating.length > 0 && (
              <div>
                <DeteriorationList assets={deteriorating} />
              </div>
            )}

            {/* ── Photos ── */}
            {photos && photos.length > 0 && (
              <div>
                <SectionHeading>Photos ({photos.length})</SectionHeading>
                <PhotoGallery photos={photos} />
              </div>
            )}

            {/* ── Children summary ── */}
            {childSummary.length > 0 && (
              <div>
                <SectionHeading>
                  {isArea
                    ? `Inspections (${childSummary.length})`
                    : isCampaign
                      ? `Areas (${childSummary.length})`
                      : `Children (${childSummary.length})`}
                </SectionHeading>
                <div className="space-y-1.5 max-h-[40vh] overflow-y-auto pr-1">
                  {childSummary.slice(0, 50).map((child) => (
                    <div
                      key={child.id}
                      className="flex items-center justify-between py-1.5 px-3
                                 rounded-lg bg-fl-sand/20 hover:bg-fl-sand/30
                                 transition-colors text-sm"
                    >
                      <span className="text-fl-evergreen/80 truncate mr-2">
                        {child.label}
                      </span>
                      {child.severity !== undefined && (
                        <span
                          className="text-xs font-semibold tabular-nums shrink-0 px-1.5 py-0.5 rounded"
                          style={{
                            color: severityColor(child.severity),
                            backgroundColor: `${severityColor(child.severity)}15`,
                          }}
                        >
                          {child.severity.toFixed(1)}
                        </span>
                      )}
                    </div>
                  ))}
                  {childSummary.length > 50 && (
                    <p className="text-xs text-fl-evergreen/50 text-center pt-1">
                      +{childSummary.length - 50} more
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ── Click hint ── */}
            {node.type !== "inspection" && !node.expanded && (
              <p className="text-xs text-fl-evergreen/50 text-center">
                Click the node to expand children
              </p>
            )}

            {/* ── Navigation hint ── */}
            <div className="pt-4 border-t border-fl-evergreen/10">
              <p className="text-[10px] text-fl-evergreen/30 text-center">
                WASD to fly · Mouse to orbit · Q/E up/down · Scroll to zoom
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── Sub-components ──

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs uppercase tracking-widest text-fl-forest/60 mb-2 font-semibold">
      {children}
    </h3>
  );
}

function MetaRow({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div className="flex justify-between items-center py-1 border-b border-fl-evergreen/10">
      <span className="text-fl-evergreen/50">{label}</span>
      <span
        className="text-fl-evergreen font-medium"
        style={color ? { color } : undefined}
      >
        {value}
      </span>
    </div>
  );
}
